// PinCode: a short numeric code typed into a row of wells, one digit each, the way a PIN is set
// for Fireflasher sign-in. Typing moves on, Backspace steps back, a paste fills the row.
// docs/operations/design-standard.md, "Controls".
//
// The value is contiguous digits from the left, so `value.length === length` means complete.
// m-0 and p-0 are load-bearing: the global input rules in index.css add margin and padding.

import React, { useImperativeHandle, useRef, useState } from 'react';

export interface PinCodeProps {
  value: string;
  onChange: (value: string) => void;
  /** The group's accessible name ("New PIN"). */
  label: string;
  length?: number;
  /** Draws each digit as a dot. On by default. */
  masked?: boolean;
  error?: boolean;
  disabled?: boolean;
  /** Focuses the first empty well on mount. */
  autoFocus?: boolean;
  /** Stops password managers filling a saved password into a new PIN ("new-password"). */
  autoComplete?: string;
  /** Runs when the last digit lands, with the whole code. */
  onComplete?: (value: string) => void;
}

const digitsOf = (s: string) => s.replace(/\D/g, '');

/** The ref focuses the first empty well, for a step that starts again after a mismatch. */
export const PinCode = React.forwardRef<{ focus: () => void }, PinCodeProps>(({
  value, onChange, label, length = 4, masked = true, error, disabled, autoFocus, autoComplete = 'off', onComplete,
}, ref) => {
  const boxes = useRef<Array<HTMLInputElement | null>>([]);
  const [first] = useState(() => Math.min(value.length, length - 1));
  // Ahead of the props between a keystroke and the parent's render: the next well's focus reads it.
  const current = useRef(value);
  current.current = value;

  const focusAt = (i: number) => {
    const el = boxes.current[Math.max(0, Math.min(i, length - 1))];
    if (el) { el.focus(); el.select(); }
  };
  useImperativeHandle(ref, () => ({ focus: () => focusAt(Math.min(current.current.length, length - 1)) }));

  const commit = (next: string) => {
    const was = current.current;
    const clean = digitsOf(next).slice(0, length);
    current.current = clean;
    onChange(clean);
    if (clean.length === length && was.length !== length) onComplete?.(clean);
    return clean;
  };

  const put = (i: number, typed: string) => {
    const was = current.current;
    const at = Math.min(i, was.length);
    const next = commit(was.slice(0, at) + typed + was.slice(at + typed.length));
    focusAt(Math.min(at + typed.length, next.length, length - 1));
  };

  const onKeyDown = (i: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    const was = current.current;
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (i < was.length) { commit(was.slice(0, i) + was.slice(i + 1)); focusAt(i); }
      else if (i > 0) { commit(was.slice(0, i - 1)); focusAt(i - 1); }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusAt(i - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusAt(Math.min(i + 1, was.length));
    }
  };

  return (
    <div role="group" aria-label={label} className="flex items-center gap-2">
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => { boxes.current[i] = el; }}
          type={masked ? 'password' : 'text'}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={autoComplete}
          autoFocus={autoFocus && i === first}
          disabled={disabled}
          aria-label={`${label}, digit ${i + 1} of ${length}`}
          aria-invalid={error || undefined}
          value={value[i] || ''}
          onFocus={(e) => {
            // The row fills from the left: a well past the next empty one hands focus back to it.
            if (i > current.current.length) focusAt(current.current.length);
            else e.currentTarget.select();
          }}
          onChange={(e) => {
            // An emptied well is a delete: Android keyboards send no usable Backspace keydown, and cut does the same.
            if (e.target.value === '') {
              const was = current.current;
              if (i < was.length) { commit(was.slice(0, i) + was.slice(i + 1)); focusAt(i); }
              return;
            }
            const typed = digitsOf(e.target.value);
            const old = current.current[i];
            if (!typed) return;
            // A filled well that was not selected reports its old digit first.
            put(i, typed.length > 1 && old && typed.startsWith(old) ? typed.slice(1) : typed);
          }}
          onPaste={(e) => {
            const pasted = digitsOf(e.clipboardData.getData('text'));
            e.preventDefault();
            if (pasted) put(i, pasted.slice(0, length - Math.min(i, current.current.length)));
          }}
          onKeyDown={onKeyDown(i)}
          className={`m-0 h-12 w-11 min-w-0 rounded-control border-0 bg-surface-container-high p-0 text-center text-xl font-semibold tabular-nums text-on-surface caret-primary shadow-recess transition-[box-shadow,background-color] duration-quick ease-emphasized focus:outline-none focus:ring-2 disabled:bg-surface-container disabled:text-on-surface-disabled disabled:shadow-none ${
            error ? 'keep-ring ring-2 ring-red-500/60 focus:ring-red-500/80' : 'focus:ring-primary/50'
          }`}
        />
      ))}
    </div>
  );
});
PinCode.displayName = 'PinCode';

export default PinCode;
